/**
 * Grouped KPI Overview Card Component
 * Organizes KPIs into Performance, Conversion Funnel, and Retention sections
 */

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useKpiData, formatPeriod } from '@/hooks/useKpiData';
import { KpiSkeleton } from './KpiSkeleton';
import { KpiDetailModal } from './KpiDetailModal';
import { KpiOverviewCardProps, KPI_CONFIG } from '@/types/kpi';
import { RefreshCw, AlertCircle, TrendingUp, Users, BarChart3, Info } from 'lucide-react';

export const GroupedKpiOverviewCard: React.FC<KpiOverviewCardProps> = ({
  sellerId,
  periodStart,
  periodEnd,
  className
}) => {
  const { data, isLoading, error, refetch } = useKpiData(sellerId, periodStart, periodEnd);
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);

  // Loading state
  if (isLoading) {
    return <KpiSkeleton className={className} />;
  }

  // Error state
  if (error || !data) {
    return (
      <div className={cn(
        "bg-red-50 dark:bg-red-900/20 backdrop-blur-md rounded-lg p-6 shadow-lg",
        "border border-red-200 dark:border-red-800",
        className
      )}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Error Loading KPIs
          </h3>
          <button
            onClick={refetch}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
            aria-label="Retry loading KPIs"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-red-600 dark:text-red-400">
          {error?.message || 'Failed to load KPI data'}
        </p>
      </div>
    );
  }

  const periodDisplay = formatPeriod(periodStart, periodEnd);

  return (
    <div className={cn(
      "bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-lg p-6 shadow-lg",
      "border border-gray-200 dark:border-gray-700",
      "transition-all duration-300 hover:shadow-xl",
      className
    )}>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            KPI Overview
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {periodDisplay}
          </span>
        </div>
        {data.calculated_at && (
          <div className="text-xs text-gray-400 dark:text-gray-500">
            Last refreshed: {new Date(data.calculated_at).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>

      {/* KPI Groups */}
      <div className="space-y-6">
        {/* Active Customers Section - FIRST */}
        {data.active_customers !== undefined && (
          <KpiSection
            title="Aktueller Status"
            icon={<Users className="w-5 h-5" />}
            iconColor="text-indigo-600 dark:text-indigo-400"
          >
            <div className="grid grid-cols-1 gap-3">
              <KpiItem
                icon={KPI_CONFIG.active_customers.icon}
                label={KPI_CONFIG.active_customers.label}
                value={KPI_CONFIG.active_customers.format(data.active_customers)}
                highlight
                tooltip={KPI_CONFIG.active_customers.tooltip}
                onClick={() => setSelectedKpi('active_customers')}
              />
            </div>
          </KpiSection>
        )}

        {/* Conversion Funnel Section - NOW SECOND */}
        <KpiSection
          title="Konversion"
          icon={<BarChart3 className="w-5 h-5" />}
          iconColor="text-green-600 dark:text-green-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KpiItem
              icon={KPI_CONFIG.lead_to_contract_cvr.icon}
              label={KPI_CONFIG.lead_to_contract_cvr.label}
              value={KPI_CONFIG.lead_to_contract_cvr.format(data.lead_to_contract_cvr)}
              trend={getTrend('lead_to_contract_cvr', data.lead_to_contract_cvr)}
              companyAverage={data.company_averages?.lead_to_contract_cvr}
              tooltip={KPI_CONFIG.lead_to_contract_cvr.tooltip}
              onClick={() => setSelectedKpi('lead_to_contract_cvr')}
            />
            <KpiItem
              icon={KPI_CONFIG.lead_to_eb_cvr.icon}
              label={KPI_CONFIG.lead_to_eb_cvr.label}
              value={KPI_CONFIG.lead_to_eb_cvr.format(data.lead_to_eb_cvr)}
              trend={getTrend('lead_to_eb_cvr', data.lead_to_eb_cvr)}
              companyAverage={data.company_averages?.lead_to_eb_cvr}
              tooltip={KPI_CONFIG.lead_to_eb_cvr.tooltip}
              onClick={() => setSelectedKpi('lead_to_eb_cvr')}
            />
            <KpiItem
              icon={KPI_CONFIG.eb_to_posting_cvr.icon}
              label={KPI_CONFIG.eb_to_posting_cvr.label}
              value={KPI_CONFIG.eb_to_posting_cvr.format(data.eb_to_posting_cvr)}
              trend={getTrend('eb_to_posting_cvr', data.eb_to_posting_cvr)}
              companyAverage={data.company_averages?.eb_to_posting_cvr}
              tooltip={KPI_CONFIG.eb_to_posting_cvr.tooltip}
              onClick={() => setSelectedKpi('eb_to_posting_cvr')}
            />
            <KpiItem
              icon={KPI_CONFIG.posting_to_contract_cvr.icon}
              label={KPI_CONFIG.posting_to_contract_cvr.label}
              value={KPI_CONFIG.posting_to_contract_cvr.format(data.posting_to_contract_cvr)}
              trend={getTrend('posting_to_contract_cvr', data.posting_to_contract_cvr)}
              companyAverage={data.company_averages?.posting_to_contract_cvr}
              tooltip={KPI_CONFIG.posting_to_contract_cvr.tooltip}
              onClick={() => setSelectedKpi('posting_to_contract_cvr')}
            />
          </div>
        </KpiSection>

        {/* Retention Section - SECOND */}
        <KpiSection
          title="Kundenbindung"
          icon={<Users className="w-5 h-5" />}
          iconColor="text-purple-600 dark:text-purple-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KpiItem
              icon={KPI_CONFIG.paused_customers.icon}
              label={KPI_CONFIG.paused_customers.label}
              value={KPI_CONFIG.paused_customers.format(data.paused_customers)}
              trend={getTrend('paused_customers', data.paused_customers, true)}
              tooltip={KPI_CONFIG.paused_customers.tooltip}
              onClick={() => setSelectedKpi('paused_customers')}
            />
            <KpiItem
              icon={KPI_CONFIG.termination_rate.icon}
              label={KPI_CONFIG.termination_rate.label}
              value={KPI_CONFIG.termination_rate.format(data.termination_rate)}
              trend={getTrend('termination_rate', data.termination_rate, true)}
              companyAverage={data.company_averages?.termination_rate}
              tooltip={KPI_CONFIG.termination_rate.tooltip}
              onClick={() => setSelectedKpi('termination_rate')}
            />
          </div>
        </KpiSection>

        {/* Performance Section - LAST */}
        <KpiSection
          title="Umsatz"
          icon={<TrendingUp className="w-5 h-5" />}
          iconColor="text-blue-600 dark:text-blue-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KpiItem
              icon={KPI_CONFIG.monthly_revenue_earned.icon}
              label={KPI_CONFIG.monthly_revenue_earned.label}
              value={KPI_CONFIG.monthly_revenue_earned.format(data.monthly_revenue_earned)}
              trend={getTrend('monthly_revenue_earned', data.monthly_revenue_earned)}
              highlight
              tooltip={KPI_CONFIG.monthly_revenue_earned.tooltip}
              onClick={() => setSelectedKpi('monthly_revenue_earned')}
            />
            <KpiItem
              icon={KPI_CONFIG.active_care_stays.icon}
              label={KPI_CONFIG.active_care_stays.label}
              value={KPI_CONFIG.active_care_stays.format(data.active_care_stays)}
              trend={getTrend('active_care_stays', data.active_care_stays)}
              tooltip={KPI_CONFIG.active_care_stays.tooltip}
              onClick={() => setSelectedKpi('active_care_stays')}
            />
            <KpiItem
              icon={KPI_CONFIG.care_days.icon}
              label={KPI_CONFIG.care_days.label}
              value={KPI_CONFIG.care_days.format(data.care_days)}
              trend={getTrend('care_days', data.care_days)}
              tooltip={KPI_CONFIG.care_days.tooltip}
              onClick={() => setSelectedKpi('care_days')}
            />
          </div>
        </KpiSection>
      </div>

      {/* KPI Detail Modal */}
      <KpiDetailModal
        isOpen={selectedKpi !== null}
        onClose={() => setSelectedKpi(null)}
        kpiType={selectedKpi}
        sellerId={sellerId}
        currentValue={selectedKpi ? data[selectedKpi as keyof typeof data] as number : 0}
      />
    </div>
  );
};

// KPI Section Component
interface KpiSectionProps {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  children: React.ReactNode;
}

const KpiSection: React.FC<KpiSectionProps> = ({ title, icon, iconColor, children }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
        <span className={cn("flex-shrink-0", iconColor)}>{icon}</span>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
};

// KPI Item Component
interface KpiItemProps {
  icon: string;
  label: string;
  value: string;
  trend?: 'up' | 'down' | 'neutral';
  highlight?: boolean;
  companyAverage?: number;
  tooltip?: string;
  onClick?: () => void;
}

const KpiItem: React.FC<KpiItemProps> = ({ icon, label, value, trend, highlight, companyAverage, tooltip, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div 
      className={cn(
        "py-4 px-4 rounded-lg transition-colors border cursor-pointer relative",
        "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 border-gray-200 dark:border-gray-600",
        highlight && "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3">
          <span className="text-xl" role="img" aria-label={label}>
            {icon}
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {tooltip && (
            <div className="relative">
              <button
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTooltip(!showTooltip);
                }}
              >
                <Info className="w-4 h-4" />
              </button>
              {showTooltip && (
                <div className="absolute z-50 w-64 p-3 text-sm bg-gray-900 text-white rounded-lg shadow-lg -top-2 right-8 transform">
                  <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 top-3 -right-1"></div>
                  {tooltip}
                </div>
              )}
            </div>
          )}
          {trend && <TrendIndicator trend={trend} />}
        </div>
      </div>
      
      <div className="flex items-end justify-between">
        <span className={cn(
          "text-lg font-bold",
          highlight ? "text-blue-700 dark:text-blue-300" : "text-gray-900 dark:text-gray-100"
        )}>
          {value}
        </span>
        
        {companyAverage !== undefined && (
          <div className="text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Firmen-Ø
            </div>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {companyAverage.toFixed(1)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Trend Indicator Component
interface TrendIndicatorProps {
  trend: 'up' | 'down' | 'neutral';
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({ trend }) => {
  if (trend === 'neutral') return null;

  return (
    <span className={cn(
      "text-xs font-medium px-1.5 py-0.5 rounded",
      trend === 'up' 
        ? "text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30" 
        : "text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30"
    )}>
      {trend === 'up' ? '↑' : '↓'}
    </span>
  );
};

// Helper function to determine trend (placeholder for future implementation)
function getTrend(kpiType: string, value: number, inverse: boolean = false): 'up' | 'down' | 'neutral' {
  // TODO: Implement trend calculation based on historical data
  // For now, return neutral
  return 'neutral';
}